/**
 * AdminTasksPage — operational tasks store (Sprint Calendar T1).
 *
 * Org-scoped admin view to create / list / update / complete / reopen /
 * delete tasks (reminders, viewings, handovers, follow-ups, etc.).
 *
 * Responsive shape per RESPONSIVE_PATTERNS.md (M2):
 *   Pattern 2 — table (md:+) ↔ card stack (< md:)
 *   Pattern 5 — filter inputs `w-full sm:w-[220px]`
 *
 * Entity links (tenant / application / unit / landlord / tenancy) are
 * NOT exposed in the T1 modal — they are a placeholder block; pickers
 * arrive in T3. The API contract already accepts them, so once the
 * pickers exist the modal slots them in without a backend change.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import ActivityCategoryFilter from "./activityCategories/ActivityCategoryFilter";
import TaskActivityCategoryCell from "./activityCategories/TaskActivityCategoryCell";
import useActivityCategoryFilterParams, {
  activityCategoryRequestParams,
} from "./activityCategories/useActivityCategoryFilterParams";
import {
  activityCategoryErrorMessage,
  isActivityCategoryFilterError,
} from "./activityCategories/activityCategoryErrors";
import { toast } from "sonner";
import {
  fetchAdminTasks,
  fetchAdminTaskById,
  deleteAdminTask,
  fetchAdminUsers,
} from "../../api/adminData";
import useEditDetailLoader from "../../hooks/useEditDetailLoader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import TaskFormModal, {
  TASK_TYPE_OPTIONS,
  PRIORITY_OPTIONS,
} from "./tasks/TaskFormModal";
// T2.4.3 — workflow buttons (start/pause/block/unblock + the
// existing complete/reopen) are now centralised in TaskStatusActions.
// AdminTasksPage no longer calls completeAdminTask / reopenAdminTask
// directly — the component owns both API surfaces so the row UI and
// the detail-page header stay in lock-step.
import TaskStatusActions from "./tasks/TaskStatusActions";
import { businessDateToIso, formatDateTime } from "../../utils/datetime";


// ──────────────────────────────────────────────────────────────────────
// Constants — page-only label maps and filter pills. The shared
// TASK_TYPE_OPTIONS / PRIORITY_OPTIONS arrays now live next to the
// modal (./tasks/TaskFormModal) and are re-imported above so this file
// only holds the page-specific derivations + status pill list.
// ──────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: "todo", label: "Offen" },
  { value: "in_progress", label: "In Arbeit" },
  { value: "blocked", label: "Blockiert" },
  { value: "done", label: "Erledigt" },
  { value: "cancelled", label: "Abgebrochen" },
];

const TASK_TYPE_LABEL = Object.fromEntries(
  TASK_TYPE_OPTIONS.map((o) => [o.value, o.label])
);
const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
);
const PRIORITY_LABEL = Object.fromEntries(
  PRIORITY_OPTIONS.map((o) => [o.value, o.label])
);

// Single-select filter row above the table.
const STATUS_FILTER_PILLS = [
  { value: "", label: "Alle" },
  { value: "todo", label: "Offen" },
  { value: "in_progress", label: "In Arbeit" },
  { value: "blocked", label: "Blockiert" },
  { value: "done", label: "Erledigt" },
];


// ──────────────────────────────────────────────────────────────────────
// Formatting helpers
// ──────────────────────────────────────────────────────────────────────

/** Convert a `<input type="date">` value to the ISO instant of SWISS
 *  midnight that day.
 *
 *  A date filter is a business boundary, so "from the 18th" has to mean
 *  the Swiss 18th no matter where the person filtering is sitting. The
 *  previous version read the value in the device's zone, which moved the
 *  cut-off by the device's offset and quietly included or dropped a few
 *  hours' worth of tasks at each end. */
function dateInputToIso(v) {
  if (!v) return null;
  return businessDateToIso(v);
}


// ──────────────────────────────────────────────────────────────────────
// Pills
// ──────────────────────────────────────────────────────────────────────

// A2 theme fix: LIGHT gets pastel token/palette tones, DARK keeps the
// previous hex appearance byte-for-byte via dark: pins. The tone
// strings are taken VERBATIM from AdminTaskDetailPage's S6 pills —
// both pages render the same three badges for the same task, and a
// second palette here would let them drift apart. The light
// `border-*-200 bg-*-50` + rounded-full combinations are DELIBERATE:
// index.css's light-mode rules restyle exactly those combos into the
// app-wide solid status badges.

const PILL_BASE =
  "inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] " +
  "font-bold uppercase tracking-[0.05em]";

const PILL_NEUTRAL =
  "border-border bg-muted text-muted-foreground " +
  "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#94a3b8]";

function StatusPill({ value }) {
  const label = STATUS_LABEL[value] || value;
  const tone =
    {
      todo:
        "border-sky-200 bg-sky-50 text-sky-700 " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#8fb3ff]",
      in_progress:
        "border-amber-200 bg-amber-50 text-amber-800 " +
        "dark:border-[#3a2f1f] dark:bg-[#1e160d] dark:text-[#f0b86e]",
      blocked:
        "border-red-200 bg-red-50 text-red-700 " +
        "dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b]",
      done:
        "border-emerald-200 bg-emerald-50 text-emerald-700 " +
        "dark:border-[#1f3a2a] dark:bg-[#0d1e15] dark:text-[#6ee7a8]",
      cancelled:
        "border-border bg-muted text-muted-foreground " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#4a5070]",
    }[value] || PILL_NEUTRAL;
  return <span className={`${PILL_BASE} ${tone}`}>{label}</span>;
}

function PriorityPill({ value }) {
  const label = PRIORITY_LABEL[value] || value;
  const tone =
    {
      low: PILL_NEUTRAL,
      medium:
        "border-sky-200 bg-sky-50 text-sky-700 " +
        "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#8fb3ff]",
      high:
        "border-amber-200 bg-amber-50 text-amber-800 " +
        "dark:border-[#3a2f1f] dark:bg-[#1e160d] dark:text-[#f0b86e]",
      urgent:
        "border-red-200 bg-red-50 text-red-700 " +
        "dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b]",
    }[value] || PILL_NEUTRAL;
  return <span className={`${PILL_BASE} ${tone}`}>{label}</span>;
}

function TaskTypePill({ value }) {
  const label = TASK_TYPE_LABEL[value] || value;
  return <span className={`${PILL_BASE} ${PILL_NEUTRAL}`}>{label}</span>;
}

/**
 * ASSIGN1 — how many people are helping, beside the one responsible.
 *
 * A count, not names: this column answers "who owns this", and a row of
 * avatars for everyone involved would compete with that answer on up to
 * 200 rows. The names are one click away on the detail page, which is
 * also the only response that carries them.
 *
 * Renders nothing at zero. A "0 Beteiligte" badge on most rows would be
 * a column of noise reporting the absence of something.
 */
function ParticipantBadge({ count }) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return (
    <span
      className={`${PILL_BASE} ${PILL_NEUTRAL} ml-2`}
      title={n === 1 ? "1 weitere beteiligte Person" : `${n} weitere beteiligte Personen`}
    >
      {n === 1 ? "1 Beteiligter" : `${n} Beteiligte`}
    </span>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Row actions
// ──────────────────────────────────────────────────────────────────────

function TaskRowActions({ task, onEdit, onStatusChanged, onDelete, busy }) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <button
        type="button"
        onClick={() => onEdit(task)}
        disabled={busy}
        className="rounded-[8px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0] dark:hover:border-[#3a3f55] dark:hover:bg-[#0d0f14]"
      >
        Bearbeiten
      </button>
      {/* T2.4.3 — owns all status transitions (start/pause/block/unblock
          + complete/reopen). Returns null for terminal "cancelled"
          tasks so the row collapses cleanly. */}
      <TaskStatusActions
        task={task}
        variant="list"
        onChanged={onStatusChanged}
        disabled={busy}
      />
      <button
        type="button"
        onClick={() => onDelete(task)}
        disabled={busy}
        className="rounded-[8px] border border-red-300 bg-red-50 px-3 py-1.5 text-[12px] font-semibold text-red-700 hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 dark:border-[#3a1f24] dark:bg-[#1e0d10] dark:text-[#ff6b6b] dark:hover:border-[#ff6b6b] dark:hover:bg-[#1e0d10]"
      >
        Löschen
      </button>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────

export default function AdminTasksPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();

  // Filters
  const [statusFilter, setStatusFilter] = useState(""); // "" | "todo" | "in_progress" | "done"
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  // RESP2e: the ONE filter that lives in the URL, so "show me the open
  // cleaning jobs" is a link. The five above stay component state —
  // migrating them is a separate slice, not a side effect of this one.
  // Whether the CURRENT error is an unusable area filter, which gets a
  // "clear it" action instead of a bare sentence.
  const [filterError, setFilterError] = useState(false);
  const {
    categories: categoryFilter,
    unassigned: unassignedFilter,
    isActive: hasCategoryFilter,
    setFilter: setCategoryFilter,
    clearFilter: clearCategoryFilter,
  } = useActivityCategoryFilterParams();

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [activeTask, setActiveTask] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Org users for the assignee dropdown
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchAdminUsers()
      .then((rows) => setUsers(Array.isArray(rows) ? rows : []))
      .catch(() => setUsers([]));
  }, []);

  const userEmailById = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u.id] = u.full_name || u.email || u.id;
    });
    return m;
  }, [users]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    setFilterError(false);
    const params = {
      limit: 200,
      offset: 0,
    };
    if (statusFilter) params.status = [statusFilter];
    if (taskTypeFilter) params.task_type = [taskTypeFilter];
    if (assigneeFilter) params.assigned_to_user_id = assigneeFilter;
    if (dueFrom) params.due_from = dateInputToIso(dueFrom);
    if (dueTo) params.due_to = dateInputToIso(dueTo);
    // Sent to the server, never applied to the rows after they arrive:
    // the total the footer prints comes from the same query, so a
    // client-side pass would make it describe more rows than are shown.
    Object.assign(
      params,
      activityCategoryRequestParams({
        categories: categoryFilter,
        unassigned: unassignedFilter,
      })
    );
    return fetchAdminTasks(params)
      .then((data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number.isFinite(data?.total) ? data.total : 0);
      })
      .catch((e) => {
        // A filter naming an area that no longer exists is not a
        // network failure and must not be offered "try again" — the
        // only thing that fixes it is clearing the filter.
        setFilterError(isActivityCategoryFilterError(e));
        setError(
          activityCategoryErrorMessage(e) ||
            e?.message ||
            "Aufgaben konnten nicht geladen werden."
        );
        setItems([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [
    statusFilter,
    taskTypeFilter,
    assigneeFilter,
    dueFrom,
    dueTo,
    categoryFilter,
    unassignedFilter,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  function handleNew() {
    setFormMode("create");
    setActiveTask(null);
    setFormOpen(true);
  }

  // PICK1a: a list row carries no link ARRAYS (TaskListItem), only the
  // legacy singular ids — opening the form on one and saving would write
  // back at most one link per type and delete the rest. So the row only
  // supplies an id; the form always gets the full TaskRead.
  const { requestEdit: handleEdit, pendingId: editPendingId } =
    useEditDetailLoader({
      fetchDetail: fetchAdminTaskById,
      onLoaded: useCallback((detail) => {
        setFormMode("edit");
        setActiveTask(detail);
        setFormOpen(true);
      }, []),
      errorMessage: "Die Aufgabe konnte nicht geladen werden.",
    });

  // T2.4.3 — TaskStatusActions handles the toast + API call itself.
  // The row's job after a successful transition is just to refetch
  // so list-only enrichment fields (assigned_to_email,
  // tenant_display_name, unit_short_id) stay current — the wire
  // response from the transition endpoint is the TaskRead detail
  // shape, not TaskListItem.
  function handleStatusChanged() {
    void load();
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteAdminTask(pendingDelete.id);
      toast.success("Aufgabe gelöscht.");
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error(err?.message || "Löschen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  function handleResetFilters() {
    setStatusFilter("");
    setTaskTypeFilter("");
    setAssigneeFilter("");
    setDueFrom("");
    setDueTo("");
    clearCategoryFilter();
  }

  const empty = !loading && items.length === 0 && !error;
  const hasFilters =
    !!statusFilter ||
    !!taskTypeFilter ||
    !!assigneeFilter ||
    !!dueFrom ||
    !!dueTo ||
    hasCategoryFilter;

  const pillBase =
    "rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  // The selection also travels via aria-pressed below, so it is never
  // communicated by colour alone.
  const pillActive =
    "border-primary bg-primary text-primary-foreground " +
    "dark:border-[#3a3f55] dark:bg-[#1e2238] dark:text-[#c5cbe0]";
  const pillInactive =
    "border-border bg-card text-muted-foreground hover:bg-muted hover:text-foreground " +
    "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#94a3b8] dark:hover:border-[#3a3f55] dark:hover:bg-[#0d0f14]";

  const filterInput =
    "block w-full sm:w-[200px] rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none " +
    "focus-visible:ring-2 focus-visible:ring-ring " +
    "dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0] dark:focus:border-[#3a3f55]";

  const labelClass =
    "mb-1 block text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground dark:text-[#4a5070]";

  return (
    <div className="mx-auto max-w-[1400px]">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="m-0 text-[22px] font-bold tracking-tight text-foreground dark:text-[#e2e4ed]">
            Aufgaben
          </h1>
          <p className="mt-1 text-[12px] text-muted-foreground dark:text-[#4a5070]">
            Operative To-dos und Termine — Besichtigungen, Übergaben,
            Bewerber-Nachfass, Erinnerungen.
          </p>
        </div>
        <button
          type="button"
          onClick={handleNew}
          className="rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
        >
          + Neue Aufgabe
        </button>
      </header>

      {/* Filter bar */}
      <div
        role="group"
        aria-label="Status filtern"
        className="mb-4 flex flex-wrap items-center gap-2"
      >
        {STATUS_FILTER_PILLS.map((p) => (
          <button
            key={p.value || "__all"}
            type="button"
            onClick={() => setStatusFilter(p.value)}
            aria-pressed={statusFilter === p.value}
            className={`${pillBase} ${
              statusFilter === p.value ? pillActive : pillInactive
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          {/* Not a <label htmlFor>: the control is a popover trigger,
              not a form field, so it carries its own aria-label. */}
          <span className={labelClass}>Bereich</span>
          <ActivityCategoryFilter
            categories={categoryFilter}
            unassigned={unassignedFilter}
            onChange={setCategoryFilter}
            onClear={clearCategoryFilter}
          />
        </div>
        <div>
          <label htmlFor="tasks-filter-type" className={labelClass}>
            Typ
          </label>
          <select
            id="tasks-filter-type"
            value={taskTypeFilter}
            onChange={(e) => setTaskTypeFilter(e.target.value)}
            className={filterInput}
          >
            <option value="">Alle Typen</option>
            {TASK_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="tasks-filter-assignee" className={labelClass}>
            Zuständig
          </label>
          <select
            id="tasks-filter-assignee"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            className={filterInput}
          >
            <option value="">Alle</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.full_name ? `${u.full_name} (${u.email})` : u.email}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="tasks-filter-due-from" className={labelClass}>
            Fällig von
          </label>
          <input
            id="tasks-filter-due-from"
            type="date"
            value={dueFrom}
            onChange={(e) => setDueFrom(e.target.value)}
            className={filterInput}
          />
        </div>
        <div>
          <label htmlFor="tasks-filter-due-to" className={labelClass}>
            Fällig bis
          </label>
          <input
            id="tasks-filter-due-to"
            type="date"
            value={dueTo}
            onChange={(e) => setDueTo(e.target.value)}
            className={filterInput}
          />
        </div>
        {hasFilters ? (
          <button
            type="button"
            onClick={handleResetFilters}
            className="rounded-[8px] border border-border bg-transparent px-3 py-2 text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
          >
            Zurücksetzen
          </button>
        ) : null}
      </div>

      {/* Both states are announced rather than only shown — a filter
          change re-renders the list without moving focus. */}
      <div aria-live="polite" aria-atomic="true">
        {error ? (
          <p role="alert" className="mb-4 text-sm text-destructive dark:text-[#ff6b6b]">
            {error}{" "}
            {filterError ? (
              <button
                type="button"
                onClick={clearCategoryFilter}
                className="underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Bereichsfilter zurücksetzen
              </button>
            ) : null}
          </p>
        ) : null}
        {loading ? (
          <p className="text-[13px] text-muted-foreground dark:text-[#4a5070]">
            Lade Aufgaben …
          </p>
        ) : null}
      </div>

      {empty ? (
        <div className="rounded-[10px] border border-dashed border-border bg-card p-8 text-center dark:border-[#1e2130] dark:bg-[#0d0f14]">
          <p className="mb-3 text-[13px] font-medium text-muted-foreground dark:text-[#94a3b8]">
            {/* Naming the area filter specifically when it is the only
                one active: "passt zu den Filtern" sends someone hunting
                through five controls for the one they set. */}
            {hasCategoryFilter && !statusFilter && !taskTypeFilter &&
            !assigneeFilter && !dueFrom && !dueTo
              ? "Keine Aufgaben für die ausgewählten Bereiche gefunden."
              : hasFilters
              ? "Keine Aufgaben passen zu den Filtern."
              : "Noch keine Aufgaben. Erste Aufgabe anlegen?"}
          </p>
          <button
            type="button"
            onClick={handleNew}
            className="rounded-[10px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-[#5b8cff] dark:text-white dark:hover:bg-[#4a7ae8]"
          >
            + Neue Aufgabe
          </button>
        </div>
      ) : null}

      {!loading && items.length > 0 ? (
        <>
          {/* Mobile: card stack */}
          <div className="block md:hidden space-y-2">
            {items.map((t) => (
              <article
                key={t.id}
                className="rounded-[10px] border border-border bg-card p-4 dark:border-[#1e2130] dark:bg-[#0d0f14]"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="m-0 min-w-0 flex-1 break-words text-[14px] font-semibold">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/tasks/${t.id}`)}
                      aria-label={`Aufgabe öffnen: ${t.title}`}
                      className="inline-flex min-h-[44px] cursor-pointer items-center rounded-sm text-left text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-[#c5cbe0]"
                    >
                      {t.title}
                    </button>
                  </h3>
                  <StatusPill value={t.status} />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <TaskTypePill value={t.task_type} />
                  <PriorityPill value={t.priority} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  {/* Full width above the two-column pairs: an area
                      label is the longest value on the card and would
                      truncate to nothing in half a phone's width. */}
                  <div className="col-span-2 min-w-0">
                    <dt className="text-muted-foreground dark:text-[#4a5070]">Bereich</dt>
                    <dd className="min-w-0 font-medium">
                      <TaskActivityCategoryCell code={t.activity_category} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground dark:text-[#4a5070]">Fällig</dt>
                    <dd className="font-medium tabular-nums text-foreground dark:text-[#c5cbe0]">
                      {formatDateTime(t.due_at)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground dark:text-[#4a5070]">Zuständig</dt>
                    <dd className="font-medium text-foreground dark:text-[#c5cbe0]">
                      {t.assigned_to_email ||
                        userEmailById[t.assigned_to_user_id] ||
                        "—"}
                      <ParticipantBadge count={t.participant_count} />
                    </dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <TaskRowActions
                    task={t}
                    busy={busy || editPendingId === String(t.id)}
                    onEdit={handleEdit}
                    onStatusChanged={handleStatusChanged}
                    onDelete={(x) => setPendingDelete(x)}
                  />
                </div>
              </article>
            ))}
          </div>

          {/* Tablet/Desktop: table */}
          <div className="hidden md:block overflow-x-auto rounded-[10px] border border-border bg-card dark:border-[#1e2130] dark:bg-[#13161f]">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-border text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground dark:border-[#1e2130] dark:text-[#4a5070]">
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Titel
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Typ
                  </th>
                  {/* RESP2f: beside Typ, because the two answer the
                      same kind of question — what KIND of work is this
                      — and reading them together is how somebody scans
                      a filtered list. */}
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Bereich
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Fällig
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Priorität
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Zuständig
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap text-right">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-border/60 hover:bg-muted/50 dark:border-[#1e2130]/60 dark:hover:bg-[#0d0f14]/60"
                  >
                    <td className="px-4 py-3 font-medium">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/tasks/${t.id}`)}
                        aria-label={`Aufgabe öffnen: ${t.title}`}
                        className="cursor-pointer rounded-sm text-left text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-[#c5cbe0]"
                      >
                        {t.title}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <TaskTypePill value={t.task_type} />
                    </td>
                    {/* Capped rather than nowrap: an organisation may
                        name an area anything, and one long label must
                        not push Aktionen off the panel. */}
                    <td className="max-w-[130px] px-4 py-3 xl:max-w-[200px]">
                      <TaskActivityCategoryCell code={t.activity_category} />
                    </td>
                    <td className="px-4 py-3 tabular-nums text-foreground dark:text-[#c5cbe0]">
                      {formatDateTime(t.due_at)}
                    </td>
                    <td className="px-4 py-3">
                      <PriorityPill value={t.priority} />
                    </td>
                    <td className="px-4 py-3 text-foreground dark:text-[#c5cbe0]">
                      <span className="whitespace-nowrap">
                        {t.assigned_to_email ||
                          userEmailById[t.assigned_to_user_id] ||
                          "—"}
                        <ParticipantBadge count={t.participant_count} />
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill value={t.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <TaskRowActions
                        task={t}
                        busy={busy || editPendingId === String(t.id)}
                        onEdit={handleEdit}
                        onStatusChanged={handleStatusChanged}
                        onDelete={(x) => setPendingDelete(x)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-4 py-2 text-[11px] text-muted-foreground dark:text-[#4a5070]">
              {items.length} von {total} Aufgaben
            </p>
          </div>
        </>
      ) : null}

      <TaskFormModal
        open={formOpen}
        mode={formMode}
        task={activeTask}
        onOpenChange={setFormOpen}
        onSaved={() => void load()}
      />

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(v) => {
          if (!v) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete
                ? `«${pendingDelete.title}» wird endgültig gelöscht. Der Audit-Log bleibt erhalten.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 dark:bg-[#ff6b6b] dark:text-white dark:hover:bg-[#e85a5a]"
            >
              {busy ? "Lösche …" : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
